
import * as React from "react";
import { toast as sonnerToast } from "sonner";

// Constants
const TOAST_LIMIT = 5;
const TOAST_REMOVE_DELAY = 1000000;
const DEBUG = false; // Set to true to enable debug logging

const log = (message, ...args) => {
  if (DEBUG) {
    console.log(`[useToast] ${message}`, ...args);
  }
};

// Action types for reducer
const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
};

// ID counter for toasts
let count = 0;
function genId() {
  count = (count + 1) % Number.MAX_VALUE;
  return count.toString();
}

// Map to track toast timeouts
const toastTimeouts = new Map();

// Function to add toast to removal queue
const addToRemoveQueue = (toastId) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: actionTypes.REMOVE_TOAST,
      toastId: toastId,
    });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
};

// Reducer for toast state
const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.ADD_TOAST:
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case actionTypes.UPDATE_TOAST:
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      };

    case actionTypes.DISMISS_TOAST: {
      const { toastId } = action;

      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id);
        });
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      };
    }
    
    case actionTypes.REMOVE_TOAST:
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
    default:
      return state;
  }
};

// State management
const listeners = [];
let memoryState = { toasts: [] };

function dispatch(action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

// Safe way to show toast with Sonner to prevent React state update issues
const showSonnerToast = (props) => {
  // Use Promise.resolve().then to push to the end of the event loop
  // This prevents React state updates during rendering
  Promise.resolve().then(() => {
    try {
      const title = typeof props === 'string' ? props : props.title;
      const description = typeof props === 'string' ? undefined : props.description;
      const variant = typeof props === 'string' ? undefined : props.variant;
      
      log(`Displaying sonner toast for ID: ${props.id || 'unknown'}`, props);
      
      sonnerToast(title, {
        description,
        duration: 5000,
        position: 'top-center',
        className: variant ? `variant-${variant}` : ''
      });
    } catch (error) {
      console.error("[Toast] Error displaying toast:", error);
    }
  });
};

// Track if we're currently processing a toast to prevent recursive calls
let isProcessingToast = false;

function toast(props) {
  if (isProcessingToast) {
    log("Preventing recursive toast call");
    return { id: '0', dismiss: () => {}, update: () => {} };
  }

  try {
    isProcessingToast = true;
    const id = genId();
    
    log(`Creating toast with ID: ${id}`, props);
    
    dispatch({
      type: actionTypes.ADD_TOAST,
      toast: {
        ...props,
        id,
        open: true,
        onOpenChange: (open) => {
          if (!open) {
            dispatch({
              type: actionTypes.DISMISS_TOAST,
              toastId: id,
            });
          }
        },
      },
    });
    
    // Show the toast via sonner
    showSonnerToast({
      ...props,
      id
    });
    
    return {
      id,
      dismiss: () => dispatch({
        type: actionTypes.DISMISS_TOAST,
        toastId: id,
      }),
      update: (props) => dispatch({
        type: actionTypes.UPDATE_TOAST,
        toast: { ...props, id },
      }),
    };
  } finally {
    isProcessingToast = false;
  }
}

// Create method variants for different toast types
toast.success = (title, description) => {
  return toast({ 
    title, 
    description, 
    variant: "success" 
  });
};

toast.error = (title, description) => {
  return toast({ 
    title, 
    description, 
    variant: "destructive" 
  });
};

toast.warning = (title, description) => {
  return toast({ 
    title, 
    description, 
    variant: "warning" 
  });
};

toast.info = (title, description) => {
  return toast({ 
    title, 
    description, 
    variant: "info" 
  });
};

// Dismiss method
toast.dismiss = (toastId) => {
  dispatch({
    type: actionTypes.DISMISS_TOAST,
    toastId,
  });
};

// React hook for accessing toast functionality
function useToast() {
  const [state, setState] = React.useState(memoryState);

  React.useEffect(() => {
    log("Hook initialized, listener added");
    listeners.push(setState);
    
    return () => {
      log("Hook cleanup, listener removed");
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, []);

  return {
    ...state,
    toast,
    dismiss: (toastId) => dispatch({
      type: actionTypes.DISMISS_TOAST,
      toastId,
    }),
  };
}

export { useToast, toast };
